import { Chip, Link, Stack, Typography } from "@mui/material";
import { useTranslate } from "react-admin";

import { type BillwerkPlanVariantFieldsFragment, BillwerkPlanVariantState } from "../../generated/graphql";

// States that are a gap to fill in Salesforce or Billwerk, as opposed to KNOWN, the unchecked id and the copy that
// cannot say yet.
const GAP_COLOR: Partial<Record<BillwerkPlanVariantState, "error" | "warning">> = {
  [BillwerkPlanVariantState.NotInBillwerk]: "error",
  [BillwerkPlanVariantState.Missing]: "warning",
  [BillwerkPlanVariantState.Ambiguous]: "warning",
  [BillwerkPlanVariantState.NotInPriceBook]: "warning",
  [BillwerkPlanVariantState.NoPriceBook]: "warning",
  [BillwerkPlanVariantState.NoProduct]: "warning",
  [BillwerkPlanVariantState.NoContract]: "warning",
};

const VariantId = ({ planVariant }: { planVariant: BillwerkPlanVariantFieldsFragment }) =>
  planVariant.url ? (
    <Link href={planVariant.url} target="_blank" rel="noopener noreferrer">
      {planVariant.planVariantId}
    </Link>
  ) : (
    <>{planVariant.planVariantId}</>
  );

// The Billwerk plan variant a contract item or price book entry bills with, or why it has none. Read-only: gaps are
// filled in Salesforce.
export const BillwerkPlanVariantValue = ({ planVariant }: { planVariant: BillwerkPlanVariantFieldsFragment }) => {
  const translate = useTranslate();
  const stateLabel = translate(`billwerkPlanVariants.states.${planVariant.state}`);
  const gapColor = GAP_COLOR[planVariant.state];
  return (
    <Stack
      component="span"
      direction="row"
      flexWrap="wrap"
      alignItems="center"
      columnGap={1}
      rowGap={0.5}
      data-testid="billwerk-plan-variant"
    >
      {planVariant.planVariantId && <VariantId planVariant={planVariant} />}
      {planVariant.state === BillwerkPlanVariantState.Known ? (
        <>
          {planVariant.name && <span>{planVariant.name}</span>}
          {planVariant.hidden && (
            <Chip size="small" variant="outlined" label={translate("billwerkPlanVariants.hidden")} />
          )}
        </>
      ) : gapColor ? (
        <Chip size="small" color={gapColor} label={stateLabel} />
      ) : (
        <Typography component="span" variant="body2" color="text.secondary">
          {stateLabel}
        </Typography>
      )}
    </Stack>
  );
};
